// src/pages/PricingPage.js
// Rates mirror Schedule A of the PHA-Web partnership agreement (June 2026).
import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Globe2, Clock, BarChart3, ShieldCheck, Palette, RefreshCw, HelpCircle } from 'lucide-react';
import { pageVariants, pageTransition } from '../utils/pageTransitions';

const PRICING_TIERS = [
    { max: 200, rate: 100 }, { max: 300, rate: 150 }, { max: 400, rate: 175 }, { max: 500, rate: 200 },
    { max: 600, rate: 225 }, { max: 700, rate: 250 }, { max: 800, rate: 275 }, { max: 900, rate: 300 },
    { max: 1000, rate: 325 }, { max: 1100, rate: 350 }, { max: 1200, rate: 375 }, { max: 1300, rate: 400 },
    { max: 1400, rate: 425 }, { max: 1500, rate: 450 }, { max: 1600, rate: 475 }, { max: 1700, rate: 500 },
    { max: 2000, rate: 525 }, { max: 2500, rate: 600 }, { max: 3000, rate: 725 }, { max: 4000, rate: 850 },
    { max: 5000, rate: 1100 }, { max: 7500, rate: 1350 }, { max: 10000, rate: 1975 },
];
const tierFor = (u) => PRICING_TIERS.find(t => u <= t.max) || PRICING_TIERS[PRICING_TIERS.length - 1];
const $ = (n) => `$${Math.round(n).toLocaleString()}`;

// Same cost model as the live page, shown as "what you spend today".
const spendToday = (units) => {
    let fte;
    if (units < 500) fte = 1; else if (units < 1000) fte = 2; else if (units < 2000) fte = 3; else if (units < 5000) fte = 4; else fte = Math.ceil(units / 1500);
    const translation = units < 500 ? 24000 : units < 2000 ? 42000 : 60000;
    const afterHours = units < 500 ? 12000 : units < 2000 ? 24000 : 36000;
    const staffTime = (fte - Math.ceil(fte * 0.5)) * 50000;
    return { translation, afterHours, staffTime, total: translation + afterHours + staffTime };
};

// Tween a number toward its target so the hero price glides instead of jumping.
const useTween = (target, ms = 350) => {
    const [v, setV] = useState(target);
    const from = useRef(target);
    useEffect(() => {
        const start = performance.now();
        const a = from.current;
        let raf;
        const tick = (now) => {
            const t = Math.min(1, (now - start) / ms);
            const e = 1 - Math.pow(1 - t, 3);
            setV(a + (target - a) * e);
            if (t < 1) raf = requestAnimationFrame(tick); else from.current = target;
        };
        raf = requestAnimationFrame(tick);
        // rAF pauses in background tabs; make sure we land on the target regardless.
        const snap = setTimeout(() => { setV(target); from.current = target; }, ms + 50);
        return () => { cancelAnimationFrame(raf); clearTimeout(snap); };
    }, [target, ms]);
    return v;
};

const FEATURES = [
    { icon: Globe2, tone: 'text-pink-600', title: '109 languages', body: 'Detects the resident\'s language from the first message and answers in it, by voice or text.' },
    { icon: Clock, tone: 'text-blue-600', title: 'Answers 24/7/365', body: 'Applications, wait lists, rent, maintenance, policies. Nights, weekends, holidays.' },
    { icon: RefreshCw, tone: 'text-amber-600', title: 'Synced nightly', body: 'We crawl your site every night so residents get today\'s answer, not last quarter\'s.' },
    { icon: BarChart3, tone: 'text-teal-600', title: 'Manager dashboard', body: 'Anonymized trends, language mix, after-hours volume, and flagged conversations for follow-up.' },
    { icon: ShieldCheck, tone: 'text-purple-600', title: 'Compliant by design', body: 'Fair Housing aligned responses, WCAG 2.2 AA, encryption in transit and at rest, 90-day auto-purge.' },
    { icon: Palette, tone: 'text-indigo-600', title: 'Looks like you', body: 'Your colors, logo, and hotline number. One snippet on your site, unlimited staff seats, and email & phone support.' },
];


const FAQ = [
    { q: "What's the contract length?", a: 'We offer flexible month-to-month contracts with 10% discounts for annual commitments.' },
    { q: 'Do we own our data?', a: 'Yes, you retain full ownership of all your data. We auto-purge after 90 days for privacy.' },
    { q: 'Is there an on-premise option?', a: 'For authorities with 5,000+ units, we offer on-premise deployment options.' },
    { q: 'How does onboarding work?', a: 'We handle everything - just email us your policies & paste one HTML snippet.' },
];

const LOGOS = [
    ['Salem', '/salem-logo.png'], ['Arlington', '/arlington-logo.png'], ['Concord', '/concord-logo.png'], ['Andover', '/andover-logo.png'],
    ['Lexington', '/lexington-logo.png'], ['Methuen', '/methuen-logo.jpg'], ['Reading', '/reading-logo.png'], ['Pittsfield', '/pittsfield-logo.webp'],
];

const PricingPage = () => {
    const [units, setUnits] = useState(100);
    const [annual, setAnnual] = useState(false);

    const tier = tierFor(units);
    const monthly = tier.rate * (annual ? 0.9 : 1);
    const shown = useTween(monthly);
    const perUnit = monthly / units;
    const spend = spendToday(units);
    const yearly = monthly * 12;
    const pct = Math.min(100, Math.max(2, (yearly / spend.total) * 100));

    return (
        <motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={pageTransition} className="min-h-screen bg-white">

            {/* ================= HERO ================= */}
            <section className="relative bg-gradient-to-br from-blue-50 via-white to-white text-gray-900 pt-32 pb-6 overflow-hidden">
                <div className="absolute inset-0 pointer-events-none" aria-hidden>
                    <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[520px] w-[900px] rounded-full bg-blue-100/70 blur-3xl" />
                </div>
                <div className="container mx-auto px-4 relative">
                    <div className="max-w-3xl mx-auto text-center">
                        <h1 className="text-base text-blue-600 font-medium mb-6">AI Chatbot + Translation Library subscription</h1>

                        <div className="flex items-start justify-center gap-1 leading-none">
                            <span className="text-4xl sm:text-5xl font-bold text-blue-600 mt-3">$</span>
                            <span className="text-[7rem] sm:text-[9rem] font-extrabold tracking-tighter tabular-nums text-gray-900">{Math.round(shown).toLocaleString()}</span>
                        </div>
                        <p className="text-2xl text-gray-600 -mt-2">per month{annual ? ', billed annually' : ''}</p>

                        <p className="mt-6 text-gray-600">
                            That's about <span className="text-gray-900 font-semibold">{perUnit >= 1 ? `$${perUnit.toFixed(2)}` : `${Math.round(perUnit * 100)}¢`}</span> per unit, per month. Everything included, no setup fee.
                        </p>

                        {/* Slider */}
                        <div className="mt-12 text-left">
                            <div className="flex items-end justify-between mb-3">
                                <label htmlFor="units" className="text-sm text-gray-600">Units your authority serves</label>
                                <span className="text-2xl font-bold tabular-nums text-gray-900">{units.toLocaleString()}</span>
                            </div>
                            <input
                                id="units" type="range" min="100" max="10000" step="50" value={units}
                                onChange={(e) => setUnits(parseInt(e.target.value, 10))}
                                className="w-full h-2 rounded-full appearance-none cursor-pointer accent-blue-500"
                                style={{
                                    touchAction: 'none',
                                    background: `linear-gradient(to right, #2563eb ${((units - 100) / 9900) * 100}%, #e5e7eb ${((units - 100) / 9900) * 100}%)`,
                                }}
                            />
                            <div className="flex justify-between text-xs text-gray-400 mt-2 tabular-nums">
                                <span>100</span><span>2,500</span><span>5,000</span><span>7,500</span><span>10,000</span>
                            </div>
                        </div>

                        {/* Billing toggle */}
                        <div className="mt-10 inline-flex items-center gap-3 bg-gray-100 rounded-full px-2 py-1.5 text-sm">
                            <button type="button" onClick={() => setAnnual(false)} className={`px-4 py-1.5 rounded-full font-medium transition ${!annual ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}>Monthly</button>
                            <button type="button" onClick={() => setAnnual(true)} className={`px-4 py-1.5 rounded-full font-medium transition ${annual ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}>
                                Annual <span className="ml-1.5 text-green-600 text-xs font-semibold">−10%</span>
                            </button>
                        </div>

                        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
                            <Link to="/contact"><button className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-2xl font-semibold text-lg transition shadow-lg">Get started</button></Link>
                            <Link to="/utility-allowances/join"><button className="w-full sm:w-auto bg-white hover:bg-blue-50 ring-2 ring-blue-600 text-blue-600 px-8 py-4 rounded-2xl font-semibold text-lg transition">Utility Allowance Schedules · $350/yr</button></Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* ================= LOGOS ================= */}
            <section className="pt-4 pb-10 border-b border-gray-100">
                <div className="container mx-auto px-4">
                    <p className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider mb-6">Housing authorities on HousingAI</p>
                    <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
                        {LOGOS.map(([name, src]) => (
                            <img key={name} src={src} alt={`${name} Housing Authority`} className="h-10 w-auto" />
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= WHAT IT COSTS VS WHAT IT REPLACES ================= */}
            <section className="py-24 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto">
                        <div className="max-w-2xl mb-14">
                            <h2 className="text-4xl font-bold text-gray-900 tracking-tight mb-4">The subscription is the small bar.</h2>
                            <p className="text-lg text-gray-600">
                                Authorities your size typically spend on translation vendors, an after-hours answering service, and staff time on routine calls. HousingAI replaces those. The comparison below is an estimate for {units.toLocaleString()} units.
                            </p>
                        </div>

                        <div className="space-y-8">
                            <div>
                                <div className="flex items-baseline justify-between mb-2">
                                    <p className="font-semibold text-gray-900">What you spend today <span className="text-gray-400 font-normal">(estimated, per year)</span></p>
                                    <p className="text-2xl font-bold text-gray-900 tabular-nums">{$(spend.total)}</p>
                                </div>
                                <div className="h-14 w-full rounded-2xl bg-gray-100 overflow-hidden flex">
                                    <div className="h-full bg-gray-300 flex items-center px-4 text-xs font-medium text-gray-700 truncate" style={{ width: `${(spend.translation / spend.total) * 100}%` }}>Translation {$(spend.translation)}</div>
                                    <div className="h-full bg-gray-400 flex items-center px-4 text-xs font-medium text-white truncate" style={{ width: `${(spend.afterHours / spend.total) * 100}%` }}>After-hours {$(spend.afterHours)}</div>
                                    {spend.staffTime > 0 && (
                                        <div className="h-full bg-gray-500 flex items-center px-4 text-xs font-medium text-white truncate" style={{ width: `${(spend.staffTime / spend.total) * 100}%` }}>Routine calls {$(spend.staffTime)}</div>
                                    )}
                                </div>
                            </div>

                            <div>
                                <div className="flex items-baseline justify-between mb-2">
                                    <p className="font-semibold text-blue-700">Chatbot + Translation Library subscription <span className="text-gray-400 font-normal">(per year)</span></p>
                                    <p className="text-2xl font-bold text-blue-700 tabular-nums">{$(yearly)}</p>
                                </div>
                                <div className="h-14 w-full rounded-2xl bg-gray-100 overflow-hidden">
                                    <motion.div animate={{ width: `${pct}%` }} transition={{ type: 'spring', stiffness: 120, damping: 20 }} className="h-full bg-blue-600 rounded-2xl" />
                                </div>
                            </div>

                            <div className="rounded-3xl bg-gradient-to-r from-green-500 to-emerald-600 text-white p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
                                <div>
                                    <p className="text-sm text-green-100">Estimated you keep, every year</p>
                                    <p className="text-5xl font-extrabold tracking-tight tabular-nums">{$(spend.total - yearly)}</p>
                                </div>
                                <p className="text-sm text-green-50 sm:max-w-xs">
                                    Your staff stay on your payroll. Only the blue bar is paid to HousingAI.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ================= FEATURES ================= */}
            <section className="py-24">
                <div className="container mx-auto px-4">
                    <div className="max-w-5xl mx-auto">
                        <div className="text-center mb-14">
                            <h2 className="text-4xl font-bold text-gray-900 tracking-tight mb-4">One plan. Nothing held back.</h2>
                            <p className="text-lg text-gray-600 md:whitespace-nowrap">No tiers to compare, no add-ons to price out. Every authority gets the whole product.</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {FEATURES.map(f => (
                                <div key={f.title} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all flex flex-col h-full">
                                    <f.icon className={`h-12 w-12 ${f.tone} mb-4`} />
                                    <h3 className="text-xl font-bold mb-3">{f.title}</h3>
                                    <p className="text-gray-600">{f.body}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ================= FAQ (original layout) ================= */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="max-w-5xl mx-auto">
                        <h3 className="text-2xl font-bold mb-8 text-center">Frequently Asked Questions</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {FAQ.map((item) => (
                                <div key={item.q} className="bg-white rounded-xl p-6 shadow-lg">
                                    <div className="flex items-start gap-4">
                                        <HelpCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                                        <div>
                                            <h4 className="font-semibold mb-2">{item.q}</h4>
                                            <p className="text-gray-600">{item.a}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ================= CLOSER ================= */}
            <section className="bg-white border-t border-gray-100 text-gray-900 py-24">
                <div className="container mx-auto px-4 text-center">
                    <p className="text-blue-600 font-medium mb-3">{units.toLocaleString()} units · {$(monthly)}/month</p>
                    <h2 className="text-4xl sm:text-5xl font-bold tracking-tight mb-8">See it answer your residents' questions.</h2>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Link to="/contact"><button className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-2xl font-semibold text-lg transition shadow-lg">Get started</button></Link>
                        <Link to="/demo"><button className="w-full sm:w-auto bg-white hover:bg-blue-50 ring-2 ring-blue-600 text-blue-600 px-8 py-4 rounded-2xl font-semibold text-lg transition">Try the live demo</button></Link>
                    </div>
                </div>
            </section>
        </motion.div>
    );
};

export default PricingPage;
